import type { components } from "@/api/schema";

/*
 * Transport-Detail — mehr nicht.
 *
 * Seit der generierte Client steht (siehe src/api/README.md), macht diese Datei
 * genau zwei Dinge, die `openapi-fetch` NICHT macht:
 *
 *   1. `transportFetch` — einen Netzwerk-Aussetzer in einen `ApiError` mit
 *      Status 0 übersetzen. `openapi-fetch` lässt die rohe `TypeError`-
 *      Rejection durch; dann müsste jeder Aufrufer zwei Fehlerarten kennen.
 *   2. `unwrap` — das `{ data, error, response }`-Tripel in „Wert oder Wurf"
 *      auflösen, weil TanStack Query den Fehlerzustand über einen Wurf lernt.
 *
 * Die Typen kommen aus `@/api/schema` und damit aus der OpenAPI-Spec des
 * Backends. Hier wird nichts mehr über Feldnamen geraten.
 *
 * Absichtlich KEINE absolute Backend-URL: Die Spec trägt den `/api`-Präfix
 * bereits in den Pfaden. Im Dev proxied Vite dorthin, in Prod serviert
 * dasselbe Go-Binary SPA und API von einer Origin.
 */

/**
 * RFC-9457-Problem-Details aus der Spec (`ErrorModel`) — aber als `Partial`.
 *
 * Der Unterschied ist inhaltlich, nicht kosmetisch: `ErrorModel` beschreibt,
 * was das Go-Backend SENDET (`type` ist dort Pflicht, weil Huma den Default
 * `about:blank` setzt). Dieser Typ beschreibt, was wir PARSEN KONNTEN — und
 * ein 502 von Traefik oder eine abgeschnittene Antwort hat nie einen
 * Huma-Handler gesehen. Wer hier Pflichtfelder verspricht, verspricht etwas
 * über eine Gegenstelle, die im Fehlerfall gerade nicht geantwortet hat.
 */
export type ProblemDetails = Partial<components["schemas"]["ErrorModel"]>;

interface ApiErrorInit {
  message: string;
  status: number;
  url: string;
  body: unknown;
  cause?: unknown;
}

/**
 * Jeder Fehlschlag — Netzwerk wie HTTP — kommt als `ApiError` an.
 *
 * `body` trägt den rohen Response-Body und ist bewusst auch bei Fehlerstatus
 * gefüllt: `/api/ready` antwortet im degradierten Fall mit 503, der Body
 * enthält dann aber die eigentlich interessante Information (welcher Check
 * gerissen ist). Ohne `body` wäre dieser Zustand nicht darstellbar.
 *
 * `problem` ist die typisierte Sicht auf denselben Body, sofern er nach
 * RFC 9457 aussieht — sonst `null`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly body: unknown;
  readonly problem: ProblemDetails | null;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = "ApiError";
    this.status = init.status;
    this.url = init.url;
    this.body = init.body;
    this.problem = toProblemDetails(init.body);
  }

  /** `true`, wenn der Request die Gegenstelle nie erreicht hat (Status 0). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Erkennt Problem-Details heuristisch: mindestens eines von `title`, `detail`
 * oder `status` muss mit passendem Typ da sein.
 *
 * Die Prüfung bleibt nötig, obwohl `ErrorModel` typisiert ist — der 503-Body
 * von `/api/ready` ist ein `ReadyBody`, KEIN Problem-Dokument, und darf hier
 * nicht fälschlich als eines gelesen werden. Der Typ sagt, was der Server
 * senden soll; diese Funktion prüft, was tatsächlich ankam.
 */
function toProblemDetails(body: unknown): ProblemDetails | null {
  const record = asRecord(body);
  if (!record) return null;

  const title = record["title"];
  const status = record["status"];
  const detail = record["detail"];

  const looksLikeProblem =
    typeof title === "string" || typeof detail === "string" || typeof status === "number";
  if (!looksLikeProblem) return null;

  const problem: ProblemDetails = {};
  if (typeof record["type"] === "string") problem.type = record["type"];
  if (typeof title === "string") problem.title = title;
  if (typeof status === "number") problem.status = status;
  if (typeof detail === "string") problem.detail = detail;
  if (typeof record["instance"] === "string") problem.instance = record["instance"];
  if (Array.isArray(record["errors"])) {
    problem.errors = record["errors"].flatMap((entry): components["schemas"]["ErrorDetail"][] => {
      const item = asRecord(entry);
      if (!item) return [];
      const parsed: components["schemas"]["ErrorDetail"] = {};
      if (typeof item["message"] === "string") parsed.message = item["message"];
      if (typeof item["location"] === "string") parsed.location = item["location"];
      if ("value" in item) parsed.value = item["value"];
      return [parsed];
    });
  }
  return problem;
}

/** Baut die Meldung für den `ApiError` aus dem, was der Server hergibt. */
function describeFailure(status: number, statusText: string, body: unknown): string {
  const problem = toProblemDetails(body);
  const fromProblem = problem?.detail ?? problem?.title;
  if (fromProblem !== undefined && fromProblem.length > 0) {
    return `HTTP ${String(status)}: ${fromProblem}`;
  }
  if (statusText.length > 0) return `HTTP ${String(status)}: ${statusText}`;
  return `HTTP ${String(status)}`;
}

/**
 * `fetch`-Implementierung für den generierten Client. Fängt genau den Fall ab,
 * den `openapi-fetch` durchreicht: die Gegenstelle hat nie geantwortet
 * (DNS, Verbindungsabbruch, Abort). Status 0 unterscheidet das von jeder
 * Server-Aussage.
 */
export async function transportFetch(request: Request): Promise<Response> {
  try {
    return await fetch(request);
  } catch (cause) {
    throw new ApiError({
      message: `Netzwerkfehler bei ${request.url}: ${
        cause instanceof Error ? cause.message : String(cause)
      }`,
      status: 0,
      url: request.url,
      body: null,
      cause,
    });
  }
}

/** Das Ergebnis-Tripel von `openapi-fetch`. */
export interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Löst das Tripel in „Wert oder Wurf" auf.
 *
 * @throws {ApiError} bei jedem Status ≥ 400 — mit erhaltenem Body.
 */
export function unwrap<T>(result: FetchResult<T>): T {
  const { data, error, response } = result;
  if (!response.ok) {
    throw new ApiError({
      message: describeFailure(response.status, response.statusText, error),
      status: response.status,
      url: response.url,
      body: error ?? null,
    });
  }
  // 204/205 liefern keinen Body; der Aufrufer typisiert das über `T`.
  return data as T;
}
